import React, { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import TableContext from './TableContext';
import { css } from '@emotion/react';
import { handyBreakpoint } from '~Styles/breakpoints';

const collapsedStyle = css`
  width: 1%;
  white-space: nowrap;
`;

const TableCell = styled.td<{
  type: string;
  collapsed?: boolean;
  dark?: boolean;
}>`
  display: table-cell;
  vertical-align: inherit;
  border-bottom: ${(props) => (props.dark ? 'none' : '1px solid rgba(224, 224, 224, 1)')};
  text-align: ${(props) => (props.align === 'right' ? 'right' : 'left')};

  padding: 14px 40px 14px 16px;
  ${handyBreakpoint} {
    padding: 8px 16px;
  }

  box-sizing: border-box;

  font-size: ${(props) => (props.type === 'headCell' ? '0.75rem' : '0.875rem')};
  color: ${(props) =>
    props.type === 'headCell'
      ? props.dark
        ? 'var(--colorBlueGray100)'
        : 'rgba(0, 0, 0, 0.54)'
      : props.dark
      ? 'var(--colorBlueGray200)'
      : 'rgba(0, 0, 0, 0.87)'};
  font-weight: ${(props) => (props.type === 'headCell' ? '500' : '400')};
  line-height: ${(props) => (props.type === 'headCell' ? '1.3125rem' : '1rem')};
  ${({ collapsed }) => collapsed && collapsedStyle};
`;

interface Props extends React.TdHTMLAttributes<HTMLTableDataCellElement> {
  component?: string;
  collapsed?: boolean;
  dark?: boolean; // Add dark prop here
}

const TableCellComponent: React.FunctionComponent<Props> = ({ component, dark, ...other }) => {
  const { variant } = useContext(TableContext);

  const tableCellType = useMemo(() => {
    let type;
    if (variant === 'head') {
      type = 'headCell';
    } else if (component === 'th') {
      type = 'bodyHeader';
    } else {
      type = 'bodyData';
    }
    return type;
  }, [variant, component]);

  const tag = useMemo(() => {
    if (tableCellType === 'headCell' || tableCellType === 'bodyHeader') {
      return 'th';
    }
    return 'td';
  }, [tableCellType]);

  return <TableCell as={tag} type={tableCellType} dark={dark} {...other} />;
};

export default TableCellComponent;
